import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Phone } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Overline, Reveal } from "@/components/Reveal";
import { CANDIDATE, COMMITMENTS, IMAGES, NEIGHBOURHOODS } from "@/data/campaign";

const HERO_LINES = ["Amin Nanji — ", "Candidate for City Councillor, ", "Ward 11, Mississauga"];

const STATS = [
  { value: "35+", label: "Years in Mississauga" },
  { value: "30+", label: "Years in Ward 11" },
  { value: "31", label: "Years in business & industry" },
  { value: "06", label: "Clear commitments" },
];

const Home = () => {
  const { scrollY } = useScroll();
  const badgeY = useTransform(scrollY, [0, 700], [0, -60]);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-paper pt-28 lg:pt-32">
        <div className="pointer-events-none absolute -right-24 top-24 hidden select-none font-serif text-[22rem] font-bold leading-none text-pine-700/5 lg:block">
          11
        </div>
        <div className="mx-auto grid max-w-7xl gap-14 px-5 pb-20 sm:px-8 lg:grid-cols-12 lg:gap-8">
          <div className="flex flex-col justify-center lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8 flex items-center gap-3"
            >
              <span className="h-px w-10 bg-terra-500" />
              <Overline>Rooted in Community · Focused on Results</Overline>
            </motion.div>

            <h1 data-testid="hero-headline" className="font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-6xl lg:text-7xl">
              {HERO_LINES.map((line, i) => (
                <span key={line} className="hero-mask-line">
                  <motion.span
                    className={`block ${i === 2 ? "italic text-terra-500" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.75 }}
              className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-pine-600"
            >
              {CANDIDATE.subline}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-6 max-w-xl text-base leading-relaxed text-ink/75 sm:text-lg"
            >
              {CANDIDATE.statement} I’m Amin Nanji, and I would be honoured to earn your trust and serve as your voice
              at City Hall.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.05 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/get-involved?tab=volunteer"
                data-testid="hero-cta-volunteer"
                className="group flex items-center gap-2 rounded-full bg-pine-700 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500"
              >
                Join the Campaign
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/get-involved?tab=priority"
                data-testid="hero-cta-priority"
                className="group flex items-center gap-2 rounded-full border border-pine-700/30 px-7 py-4 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500"
              >
                Share Your Priority
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/donate"
                data-testid="hero-cta-donate"
                className="group flex items-center gap-2 rounded-full bg-blue-700 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
              >
                Donate
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          <div className="relative min-w-0 lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="img-frame relative w-full max-w-full overflow-hidden"
            >
              <motion.img
                src={IMAGES.family}
                alt="Amin Nanji with his family at home in Ward 11"
                data-testid="hero-portrait"
                className="block h-auto w-full max-w-full object-cover"
              />
            </motion.div>

            <motion.div
              style={isDesktop ? { y: badgeY } : undefined}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="relative mt-4 box-border w-full max-w-full border border-pine-700/10 bg-paper/90 p-4 shadow-xl backdrop-blur-md sm:absolute sm:-left-10 sm:bottom-10 sm:mt-0 sm:w-auto sm:max-w-[240px] sm:p-5"
              data-testid="hero-quote-badge"
            >
              <p className="font-serif text-base italic leading-snug text-pine-700">
                “The best ideas come from the people who live here. I’ll listen carefully and work tirelessly to turn
                those ideas into action.”
              </p>
              <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50">— Amin Nanji</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.35 }}
              className="absolute right-4 top-4 flex items-center gap-2 bg-pine-900/85 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-paper backdrop-blur"
            >
              <MapPin className="h-3 w-3 text-terra-400" /> Ward 11 · Mississauga
            </motion.div>
          </div>
        </div>

        <div className="border-t border-pine-700/10">
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-pine-700/10 px-5 sm:px-8 lg:grid-cols-4" data-testid="hero-stats">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="px-5 py-8"
              >
                <p className="font-serif text-4xl font-semibold text-pine-700">{s.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-ink/55">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* MEET THE CANDIDATE TEASER */}
      <section data-testid="meet-teaser" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="min-w-0 lg:col-span-5">
            <div className="img-frame w-full max-w-full overflow-hidden">
              <img src={IMAGES.flyer} alt="Amin Nanji — Rooted in Community. Focused on Results. Vote Nanji for Ward 11" loading="lazy" className="block h-auto w-full max-w-full object-cover" />
            </div>
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>Meet the Candidate</Overline>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Experience that puts <span className="italic text-terra-500">residents first.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
                The best ideas come from the people who live here. I’ll listen carefully and work tirelessly to turn
                those ideas into action
              </p>
              <Link
                to="/about"
                data-testid="meet-teaser-link"
                className="group mt-8 inline-flex items-center gap-2 border-b-2 border-terra-500 pb-1 text-sm font-semibold text-pine-700 transition-colors hover:text-terra-500"
              >
                Read Amin’s full story
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CANDIDATE OVERVIEW */}
      <section data-testid="candidate-overview" className="border-t border-pine-700/10">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <Reveal>
            <Overline>The 2026 Election</Overline>
            <h2 className="mt-4 max-w-3xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              Ward 11 Mississauga City Councillor candidate
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
              Amin Nanji is a candidate for Ward 11 in the Mississauga municipal election 2026, running to serve the
              neighbourhoods he has called home for three decades — from Streetsville to Meadowvale Village and every
              street in between. This campaign is built the same way his life here has been: listen first, then work
              tirelessly to turn what neighbours share into action at City Hall.
            </p>
            <Link
              to="/commitments"
              data-testid="candidate-overview-link"
              className="group mt-8 inline-flex items-center gap-2 border-b-2 border-terra-500 pb-1 text-sm font-semibold text-pine-700 transition-colors hover:text-terra-500"
            >
              See the six commitments
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* COMMITMENTS SNAPSHOT */}
      <section data-testid="commitments-snapshot" className="bg-pine-900 py-24 text-paper">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Overline light>My Commitments to Ward 11</Overline>
              <h2 className="mt-4 max-w-xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                Practical priorities. <span className="italic text-terra-400">Clear accountability.</span>
              </h2>
            </div>
            <Link
              to="/commitments"
              data-testid="snapshot-view-all-link"
              className="group flex items-center gap-2 border border-paper/25 px-6 py-3 text-sm font-semibold transition-colors hover:border-terra-400 hover:text-terra-400"
            >
              View all six
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <div className="mt-14 grid gap-px bg-paper/10 sm:grid-cols-2 lg:grid-cols-3">
            {COMMITMENTS.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.06}>
                <Link
                  to={`/commitments/${c.slug}`}
                  data-testid={`commitment-card-${c.number}`}
                  className="group flex h-full flex-col bg-pine-900 p-8 transition-colors duration-300 hover:bg-pine-800"
                >
                  <span className="font-mono text-sm text-terra-400">{c.number}</span>
                  <h3 className="mt-4 font-serif text-2xl font-medium leading-snug tracking-tight">{c.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-paper/65">{c.short}</p>
                  <span className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-paper/50 transition-colors group-hover:text-terra-400">
                    Read the plan <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY TEASER */}
      <section data-testid="community-teaser" className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
        <Reveal>
          <Overline>Our Community</Overline>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            Ward 11 should help shape the decisions <span className="italic text-terra-500">that affect it.</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
            Distinct and proud communities — each with its own character, history, and challenges. What they share is
            the right to a councillor who listens before decisions are made, not after.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {NEIGHBOURHOODS.map((n, i) => (
            <Reveal key={n.name} delay={i * 0.07}>
              <Link to="/ward11" data-testid={`teaser-neighbourhood-${n.name.toLowerCase().replace(/\s+/g, "-")}`} className="group block">
                <div className="w-full max-w-full overflow-hidden">
                  <img src={n.img} alt={n.name} loading="lazy" className="block aspect-[4/3] w-full max-w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-terra-500">{n.tag}</p>
                <h3 className="mt-1 font-serif text-xl font-semibold tracking-tight">{n.name}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA BAND */}
      <section data-testid="cta-band" className="border-y border-pine-700/10 bg-parch py-24">
        <div className="mx-auto max-w-7xl px-5 text-center sm:px-8">
          <Reveal>
            <Overline>Join the Effort</Overline>
            <h2 className="mx-auto mt-4 max-w-3xl font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Our neighbourhoods are worth fighting for. <span className="italic text-terra-500">Let’s get to work.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-base text-ink/70">
              Sign up to volunteer, request a lawn sign, receive campaign updates, or share the issue that matters most
              on your street.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/get-involved?tab=volunteer" data-testid="cta-volunteer-button" className="bg-pine-700 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500">
              Volunteer
            </Link>
            <Link to="/get-involved?tab=lawn_sign" data-testid="cta-lawnsign-button" className="border border-pine-700/30 px-7 py-4 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500">
              Request a Lawn Sign
            </Link>
            <Link to="/get-involved?tab=priority" data-testid="cta-priority-button" className="border border-pine-700/30 px-7 py-4 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500">
              Share Your Priority
            </Link>
            <Link to="/contact" data-testid="cta-contact-button" className="border border-pine-700/30 px-7 py-4 text-sm font-semibold text-pine-700 transition-colors hover:border-terra-500 hover:text-terra-500">
              Contact Amin
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ink/50">
              <Phone className="h-3.5 w-3.5 text-terra-500" /> Or call directly · {CANDIDATE.phone}
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
